import { useRef, useState } from 'react';
import { Upload, ImageIcon, X, Loader2 } from 'lucide-react';
import { Button } from '@nepal-ecom/ui';
import { toast } from 'sonner';
import { cmsFetch, cmsUrl } from '@/lib/cms';

const STOREFRONT_URL = (import.meta.env.VITE_STOREFRONT_URL as string | undefined) ?? 'http://localhost:3000';
// Relative path — proxied to customer-web by the admin's own nginx, and
// cmsFetch attaches the admin token the upload endpoint now requires.
const UPLOAD_URL = cmsUrl('upload');

interface Props {
  label?: string;
  value: string;
  onChange: (url: string) => void;
  /** If true, URL resolves against storefront origin when previewing (for `/uploads/...` paths). */
  resolveRelative?: boolean;
  aspect?: string;
  helperText?: string;
}

function resolveUrl(url: string) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${STOREFRONT_URL}${url}`;
  return url;
}

export function ImageUploader({
  label = 'Image',
  value,
  onChange,
  aspect = 'aspect-[16/9]',
  helperText,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [broken, setBroken] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    setBroken(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await cmsFetch(UPLOAD_URL, { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const { url } = (await res.json()) as { url: string };
      onChange(url);
      toast.success('Uploaded');
    } catch (err) {
      toast.error('Upload failed', { description: (err as Error).message });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const previewUrl = resolveUrl(value);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-xs text-slate-400 hover:text-red-500 inline-flex items-center gap-1"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      <div
        className={`${aspect} relative rounded-lg overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center`}
      >
        {value && !broken ? (
          <img
            src={previewUrl}
            alt={label}
            onError={() => setBroken(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center px-4">
            <ImageIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-500">{broken ? 'Preview failed to load' : 'No image yet'}</p>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-primary-600 animate-spin" />
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
      >
        <Upload className="h-3.5 w-3.5" /> {uploading ? 'Uploading…' : value ? 'Replace image' : 'Upload image'}
      </Button>

      {helperText && <p className="text-[11px] text-slate-400">{helperText}</p>}
    </div>
  );
}
